import Cookie from './utilities/cookie/cookie';

const trackingKeys: { [key: string]: string } = {
    utmCampaign: 'utm_campaign',
    utmId: 'utm_id',
    fbclid: 'fbclid',
    msclkid: 'msclkid',
    utmMedium: 'utm_medium',
    utmContent: 'utm_content',
    utmSource: 'utm_source',
    utmTerm: 'utm_term',
    gclId: 'gclid',
    source: 'source',
    referral: 'referral',
};

// Returns the trimmed value, or an empty string if it is missing/blank.
const clean: (value: string) => string = (value: string): string => (value && value.trim() !== '' ? value.trim() : '');

// Captures UTM/click parameters from the URL and persists them in the utmData cookie,
// preferring the Meta pixel's _fbc/_fbp cookies. The pixel sets _fbc asynchronously
// (a couple of seconds after load), so we poll for it rather than guessing a timestamp.
const initUtmTracking: () => void = (): void => {
    const urlParams: URLSearchParams = new URLSearchParams(window.location.search);
    const baseData: { [key: string]: string } = {};

    for (const snakeCaseKey of Object.values(trackingKeys)) {
        const paramValue: string = clean(urlParams.get(snakeCaseKey));

        if (paramValue) {
            baseData[snakeCaseKey] = paramValue;
        }
    }

    // Build and persist utmData. Returns true once the real _fbc cookie has been captured.
    const writeUtmData: (isFinalAttempt: boolean) => boolean = (isFinalAttempt: boolean): boolean => {
        const data: { [key: string]: string } = { ...baseData };

        const fbc: string = clean(Cookie.get('_fbc'));
        const hasFbc: boolean = !!fbc;

        if (hasFbc) {
            data.fbclid = fbc;
        } else if (data.fbclid && isFinalAttempt) {
            // Pixel never set _fbc (blocked/disabled) — construct the format as a last resort.
            data.fbclid = 'fb.1.' + Date.now() + '.' + data.fbclid;
        }

        const fbp: string = clean(Cookie.get('_fbp'));

        if (fbp) {
            data.fbp = fbp;
        }

        // Merge so we do not overwrite params captured on an earlier page view.
        let mergedData: { [key: string]: string } = data;
        const existingCookie: string = Cookie.get('utmData');

        if (existingCookie) {
            try {
                mergedData = { ...JSON.parse(decodeURIComponent(existingCookie)), ...data };
            } catch (e) {
                // Malformed cookie — keep the freshly built data (already assigned above).
            }
        }

        if (Object.keys(mergedData).length > 0) {
            Cookie.set('utmData', JSON.stringify(mergedData), 24 * 30);
        }

        return hasFbc;
    };

    if (!writeUtmData(false) && baseData.fbclid) {
        let attempts: number = 0;
        const maxAttempts: number = 20;

        const poll: number = window.setInterval((): void => {
            attempts += 1;

            if (writeUtmData(attempts >= maxAttempts) || attempts >= maxAttempts) {
                window.clearInterval(poll);
            }
        }, 500);
    }
};

export default initUtmTracking;
